import type { ContentCollapseOptions, CountUpParams, CreatedDecorativeLinesOptions } from "@common/types/commonTypes/functions";
import { CountUp } from "countup.js";
import { hasKey, isPlainObject } from "./verification";
import { slideToggle } from "./gsapUtils";
import { reactive, watch } from "vue";
import type Lenis from "lenis";
// import { gsap } from "gsap";
// import { SplitText } from "gsap/SplitText";

export function importGoogleTranslate(opts?: GoogleTranslate) {
  const {
    layout = "VERTICAL",
    trigger = "language-trigger",
    wrapper = "language-wrapper",
    src = "//translate.google.com/translate_a/element.js?cb=loadPlugin",
    renderCSS = false,
  } = opts || {};
  const buttonTrigger = document.getElementById(trigger) as HTMLElement;
  const pluginContainer = document.getElementsByClassName(wrapper)[0] as HTMLElement;
  const tag = document.createElement("script");
  const styleSheel = document.createElement("style");
  // styleSheel.type = "text/css";
  styleSheel.innerHTML = "";
  tag.async = true;
  tag.defer = true;
  tag.src = src;
  tag.onerror = () => {
    console.error("Failed to load Google Translate script");
  };
  document.getElementsByTagName("head")[0].appendChild(tag);
  if (renderCSS) {
    document.getElementsByTagName("head")[0].appendChild(styleSheel);
  }
  const loadPlugin = () => {
    try {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          layout: window.google.translate.TranslateElement.InlineLayout[layout],
          includedLanguages: opts?.includedLanguages,
          autoDisplay: opts?.autoDisplay || false,
        },
        "google_translate_element",
      );
      buttonTrigger?.addEventListener("click", () => {
        buttonTrigger.style.display = "none";
        pluginContainer.style.display = "block";
      });
      window.loadPlugin = null;
    } catch (error) {
      console.error("Error initializing Google Translate plugin:", error);
    }
  };
  window.loadPlugin = loadPlugin;
}
export function countUp(): Promise<void>;
export function countUp(targetOrParams: string | CountUpParams): Promise<void>;
export function countUp(targetOrParams: string, opts: Omit<CountUpParams, "target">): Promise<void>;
export async function countUp(targetOrParams?: string | CountUpParams, opts?: Omit<CountUpParams, "target">): Promise<void> {
  let List: JQuery<HTMLElement>;
  let params: CountUpParams = { target: ".counter", ...opts };
  // 处理只有一个 string 参数的情况
  if (typeof targetOrParams === "string") {
    List = $(targetOrParams); // 使用传入的 string 作为选择器
  }
  // 处理只有一个 CountUpParams 参数的情况
  else if (targetOrParams && isPlainObject(targetOrParams) && hasKey("target", targetOrParams)) {
    List = $(targetOrParams.target); // 使用 targetOrParams 中的 target 属性
    params = { ...params, ...targetOrParams }; // 合并传入的 CountUpParams 和默认值
  }
  // 如果没有提供 target，默认使用 ".counter"
  else {
    List = $(".counter");
  }

  // 如果没有找到目标元素，返回
  if (!List.length) return;

  // 合并默认参数和传入的参数
  const defaults = {
    startVal: 0,
    enableScrollSpy: true,
    duration: 0.7,
    useEasing: false,
    useGrouping: false,
    scrollSpyDelay: 0,
    ...params,
  };
  const { beforeInit } = params;
  if (beforeInit && typeof beforeInit === "function") {
    await beforeInit(defaults, List);
  }
  // 执行每个元素上的计数动画
  List.each((_i, item) => {
    const Num = Number($(item).text());
    if (!Num || typeof Num !== "number") return;
    new CountUp(item, Num, defaults);
  });
}

/* 点击展开简陋版本（filpHeight） */
export function filpHeight(btn: string | HTMLElement, content: string | HTMLElement, L: number) {
  const BTN = typeof btn === "string" ? $(btn) : $(btn as HTMLElement);
  const CONTENT = typeof content === "string" ? $(content) : $(content as HTMLElement);
  if (!CONTENT.length || !BTN.length) return;
  BTN.addClass("active");
  CONTENT.css("height", "auto");
  const LH = (CONTENT.css("lineHeight") ? parseInt(CONTENT.css("lineHeight").replace("px", "")).toFixed(2) : 32) as number;
  const H = CONTENT.outerHeight() || 0;
  CONTENT.css("-webkit-line-clamp", L + "");
  const minH = CONTENT.height() as number;
  const showH = minH == H ? (L * LH > minH ? minH : L * LH) : minH;
  if (H <= showH) {
    BTN.css("display", "none");
    return;
  }
  CONTENT.css("-webkit-line-clamp", "unset");
  CONTENT.css("transition", ".3s ease-out");
  function filp() {
    CONTENT.toggleClass("active");
    BTN.toggleClass("active");
    const FLAG = CONTENT.hasClass("active");
    if (FLAG) {
      CONTENT.css("height", showH + "px");
      CONTENT.css("overflow", "hidden");
      setTimeout(function () {
        CONTENT.css("-webkit-line-clamp", L + "");
      }, 300);
    } else {
      CONTENT.css("height", H + "px");
      CONTENT.css("-webkit-line-clamp", "unset");
    }
  }
  filp();
  BTN.on("click", filp);
}

/**
 * @function useContentCollapse
 * @description 多数用于SEO内容的折叠展开功能
 * @param articles 折叠内容的外层容器元素
 * @param {ContentCollapseOptions} options 可选配置选项
 * @example
 * useContentCollapse($(".productList-7 .productList-7_SEO"));
 */
export const useContentCollapse = (articles: JQuery<HTMLElement>, options?: Partial<ContentCollapseOptions>) => {
  const { activeClass = "is-expanded", on, defaultHeight = 100 } = options || {};
  const expendHeight = (article: HTMLElement, expendHeight?: number) => {
    return expendHeight ? $(article).data("_expandHeight_", expendHeight) : $(article).data("_expandHeight_");
  };
  const getELs = (article: HTMLElement) => {
    const $trigger = $(article).find("*[trigger]");
    const $content = $(article).find("*[content]");
    $trigger.removeAttr("trigger");
    $content.removeAttr("content");
    return { $trigger, $content };
  };
  const getState = (article: HTMLElement) => $(article).hasClass(activeClass);
  const toggleState = async (article: HTMLElement, $content: JQuery<HTMLElement>, action: boolean, initialization?: boolean) => {
    const H = expendHeight(article);
    const afterCallback = action ? on?.expanded : on?.collapsed;
    const beforeCallback = action ? on?.expand : on?.collapse;
    beforeCallback?.(article, $content[0]);
    $(article).toggleClass(activeClass, action);
    await slideToggle($content[0], action, {
      fromHeight: defaultHeight,
      duration: initialization ? 0 : H / defaultHeight / 10,
    });
    afterCallback?.(article, $content[0]);
    if (action) {
      expendHeight(article, $content.outerHeight() || 0);
    }
  };
  const destroy = (article: HTMLElement) => {
    $(article).addClass("no-expand");
  };
  const init = (article: HTMLElement, $trigger: JQuery<HTMLElement>, $content: JQuery<HTMLElement>) => {
    $trigger.on("click", (event) => {
      event.preventDefault();
      const action = !getState(article);
      toggleState(article, $content, action);
    });
    getState(article) ? toggleState(article, $content, true, true) : toggleState(article, $content, false, true);
  };
  articles.each((index, article) => {
    const { $trigger, $content } = getELs(article);
    if (!$trigger.length || !$content.length) return;
    expendHeight(article, $content.outerHeight());
    expendHeight(article) < defaultHeight ? destroy(article) : init(article, $trigger, $content);
  });
};

/*! 平均分圆 */
export function circleRender(that: string) {
  const PARENT = $(that).parent().parent();
  const ITEM = $(that);
  const r = PARENT.width()! / 2;
  const COUNT = ITEM.length;
  const pieceDeg = 360 / COUNT;
  for (let i = 0; i < COUNT; i++) {
    let t = 180 - i * pieceDeg;
    t = (Math.PI / 180) * t;
    const X = $(ITEM[i]).outerWidth()! / 2;
    const Y = $(ITEM[i]).outerHeight()! / 2;
    const x = Math.sin(t) * r - X;
    const y = Math.cos(t) * r - Y;
    $(ITEM[i]).css({
      transform: `translate(${x}px,${y}px)`,
    });
  }
}

/**
 * @description 创建装饰性线条
 * @param options 配置项
 *  @param options.container 线条容器
 *  @param options.count 线条数量，默认 6
 *  @param options.beginning 是否从头部开始，默认 true
 *  @param options.direction 线条方向，默认 horizontal
 */
export const createdDecorativeLines = (options: CreatedDecorativeLinesOptions) => {
  const { container, count = 6, beginning = true, direction = "horizontal" } = options;
  // const { width } = container.getBoundingClientRect();
  const linesBox = document.createElement("div");
  linesBox.style.pointerEvents = "none";
  linesBox.className = "decorative-lines-wrapper";
  linesBox.setAttribute("aria-hidden", "true");
  container.appendChild(linesBox);
  const line = document.createElement("div");
  line.className = "decorative-line";
  line.setAttribute("aria-hidden", "true");
  for (let i = 0; i < count; i++) {
    const cloneLine = line.cloneNode() as HTMLElement;
    cloneLine.style[direction === "horizontal" ? "left" : "top"] = ((i + (beginning ? 0 : 1)) / (count - (beginning ? 1 : -1))) * 100 + "%";
    linesBox.appendChild(cloneLine);
  }
};

type Join<K, P> = K extends string | number ? (P extends string | number ? `${K}${"" extends P ? "" : "."}${P}` : never) : never;
type Prev = [never, 0, 1, 2, 3, 4, 5, ...0[]];
type IsLeaf<T> = T extends HTMLElement
  ? true
  : T extends Lenis
  ? true
  : T extends () => void
  ? true
  : T extends readonly unknown[]
  ? true
  : T extends object
  ? false
  : true;
type Paths<T, D extends number = 3> = [D] extends [never]
  ? ""
  : IsLeaf<T> extends true
  ? ""
  : { [K in keyof T & string]: K | Join<K, Paths<T[K], Prev[D]>> }[keyof T & string];

type PathValue<T, P extends string> = P extends `${infer Key}.${infer Rest}`
  ? Key extends keyof T
    ? PathValue<T[Key], Rest>
    : never
  : P extends keyof T
  ? T[P]
  : never;

interface GlobalState {
  lenis: Lenis | null;
  currentDirection: -1 | 1 | 0;
  header: {
    el: HTMLElement | null;
    inTouch: boolean;
    visible: boolean;
    fixed: boolean;
    elements: Array<{
      el: HTMLElement;
      visible: boolean;
    }>;
  };
}

interface GlobalStateHeightOptions {
  totalHeight: string;
  visibleHeight: string;
}

/**
 * @function useGlobalState
 * @description 初始化全局状态单例。重复调用时不会重新创建 proxy 或重复注册监听，只会复用同一份状态并刷新高度 CSS 变量配置。
 * @param options header 高度写入的 CSS 变量名
 * @param options.totalHeight header 总高度变量名
 * @param options.visibleHeight header 可见高度变量名
 * @returns 全局状态 proxy、状态更新方法和高度读取方法
 * @example
 * const { state, updateState, getHeights } = initGlobalState({
 *   totalHeight: "--header-total-height",
 *   visibleHeight: "--header-visible-height",
 * });
 */
export const useGlobalState = (options?: Partial<GlobalStateHeightOptions>) => {
  type GlobalStateCache = {
    state: GlobalState;
    heightOptions: GlobalStateHeightOptions;
    watchReady: boolean;
    api?: {
      state: GlobalState;
      updateState: <P extends Paths<GlobalState>>(path: P, value: PathValue<GlobalState, P>) => void;
      getHeights: () => { total: number; visible: number };
    };
  };
  const DEFAULT_OPTIONS = {
    totalHeight: "--header-total-height",
    visibleHeight: "--header-visible-height",
  };
  const globalWindow = window as Window & { __yigeGlobalState__?: GlobalStateCache };
  if (!globalWindow.__yigeGlobalState__) {
    globalWindow.__yigeGlobalState__ = {
      heightOptions: { ...DEFAULT_OPTIONS, ...options },
      watchReady: false,
      state: reactive<GlobalState>({
        lenis: null,
        currentDirection: 0,
        header: {
          el: null,
          inTouch: false,
          visible: false,
          elements: [],
          fixed: false,
        },
      }) as GlobalState,
    };
  }

  const cache = globalWindow.__yigeGlobalState__!;
  cache.heightOptions = { ...DEFAULT_OPTIONS, ...options };

  /**
   * 同步 header 总高度与可见高度到 CSS 变量。
   */
  const syncHeaderHeights = () => {
    let total = 0;
    let visible = 0;

    const headerElements = cache.state.header.elements.length
      ? cache.state.header.elements
      : cache.state.header.el
      ? [{ el: cache.state.header.el, visible: cache.state.header.visible }]
      : [];

    for (const item of headerElements) {
      const height = item.el?.offsetHeight || 0;
      total += height;
      if (item.visible) visible += height;
    }

    const htmlStyle = document.documentElement.style;

    htmlStyle.setProperty(cache.heightOptions.totalHeight, `${total}px`);
    htmlStyle.setProperty(cache.heightOptions.visibleHeight, `${visible}px`);
  };

  if (!cache.watchReady) {
    cache.watchReady = true;
    watch(
      () => [cache.state.header.el, cache.state.header.visible, ...cache.state.header.elements.map(({ el, visible }) => [el, visible] as const)] as const,
      () => syncHeaderHeights(),
    );
  }

  if (!cache.api) {
    cache.api = {
      state: cache.state,

      /**
       * 按路径更新全局状态，保持类型提示并复用同一份 reactive proxy。
       */
      updateState<P extends Paths<GlobalState>>(path: P, value: PathValue<GlobalState, P>): void {
        const keys = path.split(".");
        const lastKey = keys.pop();
        if (!lastKey) return;

        let target = cache.state as unknown as Record<string, unknown>;
        for (const key of keys) {
          target = target[key] as Record<string, unknown>;
        }
        target[lastKey] = value;
      },

      /**
       * 从 HTML 根节点 CSS 变量中读取当前 header 高度。
       */
      getHeights() {
        const htmlStyle = document.documentElement.style;
        return {
          total: parseFloat(htmlStyle.getPropertyValue(cache.heightOptions.totalHeight)) || 0,
          visible: parseFloat(htmlStyle.getPropertyValue(cache.heightOptions.visibleHeight)) || 0,
        };
      },
    };
  }

  syncHeaderHeights();
  return cache.api;
};
